import Vue from 'vue';
import Router from 'vue-router';
import i18n from '@/lang/lang';
import LoggedUserManager from '@/services/LoggedUserManager';
import { CustomerPermissions } from '@/services/permissions/CustomerPermissions';
import { AdminPermissions } from '@/services/permissions/AdminPermissions';
import ApiUserRoles, { SystemUserRoles } from './entities/enums/UserRoles';
import UserSettingsRepository from './services/repository/UserSettingsRepository';
import AppConfig from '@/configLoader';
import CookiesStorage from './services/CookiesStorage';
import DepartmentRepository from './services/repository/DepartmentRepository';
import { vxm } from '@/store/store.vuex';
import UserRepository from './services/repository/UserRepository';
import { AdminDepartment } from './entities/models/AdminDepartment';
import { FeaturesManager } from '@/entities/tiers/FeaturesManager';
import EventTypeRepository from '@/services/repository/EventTypeRepository';
import VueUtilities from './services/VueUtilities';
import { setFaroUser, logError } from './services/logging/FaroLogger';
import { TelemetryErrorTypes } from './services/logging/TelemetryEvents';
import ApplicationTypes from './entities/enums/ApplicationTypes';
import { Features } from './entities/tiers/Features';
var vxDepartmentStore = vxm.departmentStore;
Vue.use(Router);
let router = new Router({
    mode: 'history',
    routes: [
        {
            path: '/:lang(en|sk|cs)?',
            component: {
                render: (h) => h('router-view')
            },
            children: [
                {
                    path: '',
                    name: 'home',
                    meta: { layout: 'with-sidebar', needsLogin: true },
                    component: { render: (h) => h('router-view') }
                },
                {
                    path: 'login',
                    name: 'login',
                    meta: { layout: 'landing-page', needsLogin: false, authPage: true, title: 'component.login.title' },
                    props: true,
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/Login.vue')
                },
                {
                    path: 'login/demo',
                    name: 'demoLogin',
                    meta: { layout: 'default', needsLogin: false },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/DemoLogin.vue')
                },
                {
                    path: 'register',
                    name: 'register',
                    meta: { layout: 'landing-page', needsLogin: false, authPage: true, title: 'component.login.register' },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/Register.vue'),
                    beforeEnter: (to, from, next) => {
                        if (AppConfig.getConfig().features.public_registration !== true && !to.query.token)
                            return next({ name: 'login' });
                        next();
                    }
                },
                {
                    path: 'registrationIncomplete',
                    name: 'registrationIncomplete',
                    meta: {
                        layout: 'landing-page',
                        needsLogin: false,
                        authPage: true,
                        title: 'component.registration_incomplete.title'
                    },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/RegistrationIncomplete.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStorage.getItem('registered') !== 'false')
                            return next({ name: 'login' });
                        next();
                    }
                },
                {
                    path: 'registrationSuccessful',
                    name: 'registrationSuccessful',
                    meta: {
                        layout: 'landing-page',
                        needsLogin: false,
                        authPage: true,
                        title: 'component.registration_successful.title'
                    },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/RegistrationSuccessful.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStorage.getItem('registered') !== 'true')
                            return next({ name: 'login' });
                        next();
                    }
                },
                {
                    path: 'verify',
                    name: 'verify',
                    meta: {
                        layout: 'landing-page',
                        needsLogin: false,
                        authPage: true
                    },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/VerifyAccount.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!to.query.token)
                            return next({ name: 'login' });
                        next();
                    }
                },
                {
                    path: 'verifyResend',
                    name: 'verifyResend',
                    meta: {
                        layout: 'landing-page',
                        needsLogin: false,
                        authPage: true,
                        title: 'component.password_recover.verify'
                    },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/VerifyResend.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!to.query.accountNotVerified)
                            return next({ name: 'login' });
                        next();
                    }
                },
                {
                    path: 'forgotPassword',
                    name: 'forgotPassword',
                    meta: {
                        layout: 'landing-page',
                        needsLogin: false,
                        authPage: true,
                        title: 'component.password_recover.forgot'
                    },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/ForgotPassword.vue')
                },
                {
                    path: 'changePassword',
                    name: 'changePassword',
                    meta: {
                        layout: 'landing-page',
                        needsLogin: false,
                        authPage: true,
                        title: 'component.password_recover.change_password'
                    },
                    component: () => import(/* webpackChunkName: "auth" */ './views/pages/authentication/ChangePassword.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!to.query.token)
                            return next({ name: 'login' });
                        next();
                    }
                },
                {
                    path: 'invite',
                    name: 'invite',
                    meta: { layout: 'default', needsLogin: false },
                    component: () => import('./views/pages/Invitation.vue')
                },
                {
                    path: 'privacyPolicy',
                    name: 'privacyPolicy',
                    meta: { layout: 'default', needsLogin: false, title: 'cookies.title' },
                    component: () => import('./views/pages/PrivacyPolicy.vue')
                },
                {
                    path: 'page_not_exist',
                    name: 'pageNotExist',
                    meta: { layout: 'default', needsLogin: false, title: 'page_not_found.title' },
                    component: () => import('./views/pages/PageNotExist.vue')
                },
                {
                    path: 'public_dashboard_not_exist',
                    name: 'publicDashboardNotExist',
                    meta: { layout: 'default', needsLogin: false, title: 'public_dashboard_not_found.title' },
                    component: () => import('./views/pages/PublicDashboardNotExist.vue')
                },
                {
                    path: 'missing_permissions',
                    name: 'missingPermissions',
                    meta: { layout: 'default', needsLogin: false, title: 'missing_permissions.title' },
                    component: () => import('./views/pages/MissingPermissions.vue')
                },
                {
                    path: 'organizations',
                    name: 'organizations',
                    meta: {
                        layout: 'organization',
                        needsLogin: true,
                        hiddenSelectInHeader: true,
                        availableForDisabledDept: true,
                        title: 'navbar.main_org'
                    },
                    component: () => import('./views/pages/OrgHomePage.vue')
                },
                {
                    path: 'profile',
                    name: 'profile',
                    meta: {
                        layout: 'organization',
                        needsLogin: true,
                        hiddenSelectInHeader: true,
                        availableForDisabledDept: true,
                        title: 'component.settings.profile.title'
                    },
                    component: () => import('./views/pages/ProfileOrganization.vue')
                },
                {
                    path: 'notifications',
                    name: 'notificationsOrg',
                    meta: {
                        layout: 'organization',
                        needsLogin: true,
                        hiddenSelectInHeader: true,
                        availableForDisabledDept: true,
                        title: 'component.notifications_list.title'
                    },
                    component: () => import('./views/pages/NotificationOrganization.vue')
                },
                {
                    path: ':departmentId/dashboard',
                    name: 'dashboard',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: true,
                        title: 'component.dashboard.title'
                    },
                    component: () => import('./views/pages/MasterDashboard.vue')
                },
                {
                    path: ':departmentId/group_dashboard',
                    name: 'groupDashboard',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.group_dashboard.title'
                    },
                    component: () => import('./views/pages/GroupDashboard.vue')
                },
                {
                    path: ':departmentId/reports',
                    name: 'reports',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.report.title'
                    },
                    component: () => import('./views/pages/Reports.vue')
                },
                {
                    path: ':departmentId/thermalMap',
                    name: 'thermalMap',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.thermal_map.title'
                    },
                    component: () => import('./views/pages/ThermalMapPage.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_THERMOMAP_EDIT)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: ':departmentId/floorPlan',
                    name: 'floorPlan',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.floorPlan.title'
                    },
                    component: () => import('./views/pages/FloorPlanPage.vue')
                },
                {
                    path: ':departmentId/notifications',
                    name: 'notifications',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.notifications_list.title'
                    },
                    component: () => import('./views/pages/NotificationsBoard.vue')
                },
                {
                    path: ':departmentId/departmentIssues',
                    name: 'departmentIssues',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.issues.title'
                    },
                    component: () => import('./views/pages/DepartmentIssues.vue')
                },
                {
                    path: ':departmentId/configuration/devices',
                    name: 'configuration_devices',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.settings.device.device_settings'
                    },
                    component: () => import('./views/pages/DeviceManagement.vue')
                },
                {
                    path: ':departmentId/configuration/loggers',
                    name: 'configuration_loggers',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.logger.configuration.title'
                    },
                    component: () => import('./views/pages/LoggerManagement.vue')
                },
                {
                    path: ':departmentId/configuration/equipment',
                    name: 'configuration_equipment',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.equipment.configuration.title'
                    },
                    component: () => import('./views/pages/EquipmentManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.isLoggedToCompany() ||
                            ApiUserRoles.COMPANY_OWNER == vxDepartmentStore.selectedDepartmentRoleKey ||
                            ApiUserRoles.COMPANY_ADMIN == vxDepartmentStore.selectedDepartmentRoleKey)
                            next();
                        else
                            next({ name: 'pageNotExist' });
                    }
                },
                {
                    path: ':departmentId/configuration/equipment/:equipmentId',
                    name: 'equipmentDetail',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.equipment.detail_title'
                    },
                    component: () => import('./views/pages/EquipmentDetail.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.isLoggedToCompany() ||
                            ApiUserRoles.COMPANY_OWNER == vxDepartmentStore.selectedDepartmentRoleKey ||
                            ApiUserRoles.COMPANY_ADMIN == vxDepartmentStore.selectedDepartmentRoleKey)
                            next();
                        else
                            next({ name: 'pageNotExist' });
                    }
                },
                {
                    path: ':departmentId/configuration/groups',
                    name: 'configuration_groups',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.settings.location.location_settings'
                    },
                    component: () => import('./views/pages/GroupManagement.vue')
                },
                {
                    path: ':departmentId/configuration/acceptanceProtocols',
                    name: 'configuration_acceptance_protocols',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'navbar.acceptanceProtocols'
                    },
                    component: () => import('./views/pages/AcceptanceProtocolManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(CustomerPermissions.ACCEPTANCE_PROTOCOLS_MANAGE))
                            next();
                        else
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                    }
                },
                {
                    path: ':departmentId/configuration/acceptanceProtocols/:protocolId',
                    name: 'acceptance_protocol_detail',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.acceptanceProtocols.detail.title'
                    },
                    component: () => import('./views/components/acceptanceProtocol/AcceptanceProtocolDetail.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(CustomerPermissions.ACCEPTANCE_PROTOCOLS_MANAGE)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: ':departmentId/configuration/pdfReports',
                    name: 'configuration_pdf_reports',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'navbar.periodicPdfReports'
                    },
                    component: () => import('./views/pages/PdfReportManagement.vue')
                },
                {
                    path: ':departmentId/configuration/csvReports',
                    name: 'configuration_csv_reports',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'navbar.periodicCsvReports'
                    },
                    component: () => import('./views/pages/CsvReportManagement.vue')
                },
                {
                    path: ':departmentId/configuration/group/create',
                    name: 'create_group',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.group.create.title'
                    },
                    component: () => import('./views/pages/CreateGroup.vue')
                },
                {
                    path: ':departmentId/configuration/logger/:locationId',
                    name: 'logger_detail',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'loggers.logger_info'
                    },
                    component: () => import('./views/pages/LoggerDetail.vue')
                },
                {
                    path: ':departmentId/configuration/group/:groupId',
                    name: 'group_detail',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.group.detail.title'
                    },
                    component: () => import('./views/pages/GroupDetail.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(CustomerPermissions.SOURCEGROUPS_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: ':departmentId/configuration/profile',
                    name: 'profile_settings',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: true,
                        title: 'component.settings.profile.title'
                    },
                    component: () => import('./views/pages/ProfileManagement.vue')
                },
                {
                    path: ':departmentId/configuration/notifications',
                    name: 'configuration_notifications',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.notification.title'
                    },
                    component: () => import('./views/pages/NotificationsConfiguration.vue')
                },
                {
                    path: ':departmentId/configuration/periodicServices',
                    name: 'periodicServices',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'navbar.periodicServices'
                    },
                    component: () => import('./views/pages/PeriodicServiceManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(CustomerPermissions.SOURCE_SERVICE_PERIODIC_MANAGE)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: ':departmentId/configuration/boundaryPresets',
                    name: 'boundaryPresets',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'navbar.boundaryPresets'
                    },
                    component: () => import('./views/pages/BoundaryPresetManagement.vue'),
                    beforeEnter: async (to, from, next) => {
                        if (featuresManager.isAvailable(Features.BOUNDARIES_PRESETS) &&
                            LoggedUserManager.checkPermissions(CustomerPermissions.BOUNDARY_PRESETS_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: ':departmentId/device/:deviceId',
                    name: 'deviceConf',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.settings.device.device_info'
                    },
                    component: () => import('./views/pages/DeviceSettings.vue')
                },
                {
                    path: ':departmentId/wizard',
                    name: 'wizard',
                    meta: {
                        layout: 'default',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'navbar.wizard'
                    },
                    component: () => import('./views/pages/Wizard.vue'),
                    beforeEnter: (to, from, next) => {
                        if (AppConfig.getConfig().features.wizard !== true) {
                            next({ name: 'pageNotExist' });
                        }
                        else if (ApiUserRoles.COMPANY_OWNER == vxDepartmentStore.selectedDepartmentRoleKey ||
                            ApiUserRoles.COMPANY_ADMIN == vxDepartmentStore.selectedDepartmentRoleKey)
                            next();
                        else
                            next({ name: 'pageNotExist' });
                    }
                },
                {
                    path: ':departmentId/company',
                    name: 'companySettings',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.settings.administration.administration_settings'
                    },
                    component: () => import('./views/pages/CompanyManagement.vue')
                },
                {
                    path: ':departmentId/company/users',
                    name: 'userManagement',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'navbar.users'
                    },
                    component: () => import('./views/pages/UserManagement.vue')
                },
                {
                    path: ':departmentId/company/users/:userId',
                    name: 'userDetail',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'users.user_detail_title'
                    },
                    component: () => import('./views/pages/UserDetail.vue')
                },
                {
                    path: ':departmentId/publicDashboardsManagement',
                    name: 'publicDashboards',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.public_dashboard.dashboard_list.title'
                    },
                    component: () => import('./views/pages/PublicDashboardsManagement.vue')
                },
                {
                    path: ':departmentId/publicDashboardsManagement/:dashboardId',
                    name: 'publicDashboardDetail',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.public_dashboard.dashboard_detail.title'
                    },
                    component: () => import('./views/pages/PublicDashboardDetail.vue')
                },
                {
                    path: ':departmentId/publicDashboard/:dashboardKey',
                    name: 'publicDashboard',
                    meta: {
                        layout: 'public',
                        needsLogin: false,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.public_dashboard.dashboard_detail.subtitle'
                    },
                    component: () => import('./views/pages/PublicDashboardPage.vue')
                },
                {
                    path: ':departmentId/about',
                    name: 'about',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: true,
                        title: 'navbar.app'
                    },
                    component: () => import('./views/pages/AboutApp.vue')
                },
                {
                    path: ':departmentId/releaseNotes',
                    name: 'releaseNotes',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: true,
                        title: 'navbar.releaseNotes'
                    },
                    component: () => import('./views/pages/ReleaseNotes.vue')
                },
                {
                    path: ':departmentId/contact',
                    name: 'contact',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: true,
                        title: 'navbar.contact'
                    },
                    component: () => import('./views/pages/ContactPage.vue')
                },
                {
                    path: ':departmentId/map',
                    name: 'map',
                    meta: { layout: 'with-sidebar', needsLogin: true, departmentScope: true },
                    component: () => import('./views/pages/MapDashboard.vue')
                },
                {
                    path: 'publicMap',
                    name: 'publicMap',
                    meta: { layout: 'public', needsLogin: false, departmentScope: false },
                    component: () => import('./views/pages/PublicSmartCityMap.vue')
                },
                {
                    path: ':departmentId/otherServices',
                    name: 'otherServices',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: true,
                        title: 'navbar.otherServices'
                    },
                    component: () => import('./views/pages/OtherServices.vue'),
                    beforeEnter: (to, from, next) => {
                        if (AppConfig.getConfig().features.extra_services !== true) {
                            next({ name: 'pageNotExist' });
                        }
                        else
                            next();
                    }
                },
                {
                    path: 'administration/companies',
                    name: 'adminCompanies',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.companies' },
                    component: () => import('./views/pages/admin/AdminCompanyManagement.vue')
                },
                {
                    path: 'administration/companies/:departmentId',
                    name: 'adminCompanyDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.component.company.detail.companyInfo.title' },
                    component: () => import('./views/pages/admin/AdminCompanyDetailManagement.vue')
                },
                {
                    path: 'administration/companies/:departmentId/issue/:issueId',
                    name: 'issueDetailAdmin',
                    meta: { layout: 'admin', needsLogin: true, title: 'component.issues.detail.title' },
                    component: () => import('./views/components/issues/IssueDetail.vue')
                },
                {
                    path: 'administration/companies/:departmentId/acceptanceProtocol/:protocolId',
                    name: 'acceptanceProtocolDetailAdmin',
                    meta: { layout: 'admin', needsLogin: true, title: 'component.acceptanceProtocols.detail.title' },
                    component: () => import('./views/components/admin/acceptanceProtocol/AdminAcceptanceProtocolDetail.vue')
                },
                {
                    path: ':departmentId/notifications/issue/:issueId',
                    name: 'issueDetail',
                    meta: {
                        layout: 'with-sidebar',
                        needsLogin: true,
                        departmentScope: true,
                        availableForDisabledDept: false,
                        title: 'component.issues.detail.title'
                    },
                    component: () => import('./views/components/issues/IssueDetail.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(CustomerPermissions.ISSUES_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: 'administration/companies/:departmentId/publicDashboard/:dashboardId',
                    name: 'adminPublicDashboardDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'component.public_dashboard.dashboard_detail.title' },
                    component: () => import('./views/pages/admin/AdminPublicDashboardDetail.vue')
                },
                {
                    path: 'administration/companies/:departmentId/equipment/:equipmentId',
                    name: 'equipmentDetailAdmin',
                    meta: { layout: 'admin', needsLogin: true, title: 'component.equipment.detail_title' },
                    component: () => import('./views/pages/admin/AdminEquipmentDetail.vue')
                },
                {
                    path: 'administration/users',
                    name: 'adminUsers',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.users' },
                    component: () => import('./views/pages/admin/AdminUserManagement.vue')
                },
                {
                    path: 'administration/users/:userId',
                    name: 'adminUserDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.component.user.detail.userInfo.title' },
                    component: () => import('./views/pages/admin/AdminUserDetailManagement.vue')
                },
                {
                    path: 'administration/devices',
                    name: 'adminDevices',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.devices' },
                    component: () => import('./views/pages/admin/AdminDeviceManagement.vue')
                },
                {
                    path: 'administration/devices/:deviceId',
                    name: 'adminDeviceDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.component.devices.detail.deviceInfo.title' },
                    component: () => import('./views/pages/admin/AdminDeviceDetailManagement.vue')
                },
                {
                    path: 'administration/devices/firmware/:firmwareId',
                    name: 'adminFirmwareDetail',
                    meta: {
                        layout: 'admin',
                        needsLogin: true,
                        title: 'admin.component.devices.firmwareList.detail.firmwareInfo.title'
                    },
                    component: () => import('./views/pages/admin/AdminFirmwareDetailManagement.vue')
                },
                {
                    path: 'administration/loggers',
                    name: 'adminLoggers',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.loggers' },
                    component: () => import('./views/pages/admin/AdminLoggerManagement.vue')
                },
                {
                    path: 'administration/loggers/:loggerId',
                    name: 'adminLoggerDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.component.loggers.detail.info.title' },
                    component: () => import('./views/pages/admin/AdminLoggerDetailManagement.vue')
                },
                {
                    path: 'administration/resellers',
                    name: 'adminResellers',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.resellers' },
                    component: () => import('./views/pages/admin/AdminResellerManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_RESELLER_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: 'administration/resellers/:resellerId',
                    name: 'adminResellerDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.component.reseller.detail.resellerInfo.title' },
                    component: () => import('./views/pages/admin/AdminResellerDetailManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_RESELLER_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: 'administration/loggers_register',
                    name: 'adminRegisterNewLoggers',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.component.loggers.registerLoggers.title' },
                    component: () => import('./views/pages/admin/AdminRegisterLoggersManagement.vue')
                },
                {
                    path: 'administration/loggers_manufacture',
                    name: 'adminManufactureLoggers',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.component.loggers.logger_manufacturing.title' },
                    component: () => import('./views/pages/admin/ManufacturingProcessPage.vue')
                },
                {
                    path: 'administration/notificationsManagement',
                    props: (route) => ({ query: 'queryValue' }),
                    name: 'adminNotifications',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.notifications' },
                    component: () => import('./views/pages/admin/AdminNotificationManagement.vue')
                },
                {
                    path: 'administration/tiers',
                    name: 'adminTiers',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.tiers' },
                    component: () => import('./views/pages/admin/AdminTiersManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_TIERS_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: 'administration/tiers/create',
                    name: 'adminCreateTier',
                    meta: { layout: 'admin', needsLogin: true, title: 'tiers.create_tier' },
                    component: () => import('./views/pages/admin/AdminTierCreate.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_TIERS_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: 'administration/tiers/:tierId',
                    name: 'adminTierDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'tiers.tier_detail' },
                    component: () => import('./views/pages/admin/AdminTierDetail.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_TIERS_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions', params: { lang: to.params.lang } });
                        }
                    }
                },
                {
                    path: 'administration/feedback',
                    name: 'adminFeedback',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.feedback' },
                    component: () => import('./views/pages/admin/AdminFeedbackManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (AppConfig.getConfig().features.feedback !== true) {
                            next({ name: 'pageNotExist' });
                        }
                        else
                            next();
                    }
                },
                {
                    path: 'administration/aboutSystem',
                    name: 'adminAbout',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.about' },
                    component: () => import('./views/pages/admin/AdminAbout.vue'),
                    beforeEnter: (to, from, next) => {
                        if (AppConfig.getConfig().features.wp_release_notes !== true) {
                            next({ name: 'pageNotExist' });
                        }
                        else
                            next();
                    }
                },
                {
                    path: 'administration/feedback/:feedbackId',
                    name: 'adminFeedbackDetail',
                    meta: { layout: 'admin', needsLogin: true, title: 'contact_form.feedback_detail' },
                    component: () => import('./views/pages/admin/AdminFeedbackDetail.vue'),
                    beforeEnter: (to, from, next) => {
                        if (AppConfig.getConfig().features.feedback !== true) {
                            next({ name: 'pageNotExist' });
                        }
                        else
                            next();
                    }
                },
                {
                    path: 'administration/system_settings',
                    name: 'systemSettings',
                    meta: { layout: 'admin', needsLogin: true, title: 'admin.sidebar.system_settings' },
                    component: () => import('./views/pages/admin/SystemSettingsManagement.vue'),
                    beforeEnter: (to, from, next) => {
                        if (LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_SYSTEM_SETTINGS_READ) ||
                            LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_SYSTEM_TASKS_READ) ||
                            LoggedUserManager.checkPermissions(AdminPermissions.ADMIN_PRODUCT_NUMBERS_READ)) {
                            next();
                        }
                        else {
                            next({ name: 'missingPermissions' });
                        }
                    }
                }
            ]
        }
    ]
});
var userSettingsRepository = UserSettingsRepository.getInstance();
var departmentRepository = new DepartmentRepository();
var userRepository = new UserRepository();
var featuresManager = FeaturesManager.getInstance();
var eventTypeRepository = EventTypeRepository.getInstance();
const originalResolve = router.resolve;
router.resolve = function (to, current, append) {
    if (typeof to === 'object' && to.name === 'dashboard' && !to.params?.departmentId) {
        const selectedDep = getSelectedDepartment() || getDepartments()?.[0];
        if (selectedDep) {
            to = { ...to, params: { ...to.params, departmentId: selectedDep.id } };
        }
    }
    return originalResolve.call(this, to, current, append);
};
router.afterEach(async (to) => {
    // ADD TITLE
    const appName = AppConfig.getConfig()?.app.identifier || ApplicationTypes.LOGHUB;
    document.title = to.meta?.title
        ? to.meta?.layout !== 'admin'
            ? `${i18n.t(to.meta.title)} | ${appName}`
            : `${i18n.t(to.meta.title)} | ${appName} ${i18n.t('app_name_admin')}`
        : to.meta?.layout === 'admin'
            ? `${appName} ${i18n.t('app_name_admin')}`
            : appName;
});
router.beforeEach(async (to, from, next) => {
    // CHECK VALID URL
    if (to.name == null) {
        return next({ name: 'pageNotExist', params: { lang: to.params.lang || 'sk' } });
    }
    // USER DOESN'T NEED TO BE LOGGED
    if (to.name == 'publicDashboard') {
        return next();
    }
    // LOAD DATA FROM STORAGE
    const loggedIn = LoggedUserManager.storageContainLoginCredentials();
    const userId = CookiesStorage.loadData(CookiesStorage.keys.API_USER_ID);
    let userRole = CookiesStorage.loadData(CookiesStorage.keys.API_USER_ROLE);
    if (!loggedIn || userId === null) {
        if (to.name !== 'login' && to.meta.needsLogin) {
            LoggedUserManager.deleteLoginData();
            LoggedUserManager.deleteAdminLoginData();
            return router.push({ name: 'login', params: { lang: to.params.lang || 'sk', toUrl: to.fullPath } });
        }
        else {
            return next();
        }
    }
    // USER SYSTEM ROLE
    let user = null;
    if (userRole === null || LoggedUserManager.getRole() === null) {
        user = await userRepository.getCurrentUser();
        if (user?.role) {
            userRole = user?.role.Key;
            LoggedUserManager.setRole(userRole);
            CookiesStorage.storeData(CookiesStorage.keys.API_USER_ROLE, userRole, Infinity);
        }
        else
            return;
    }
    let userSettings = await userSettingsRepository.loadUserSettings();
    // LANGUAGE SETUP
    let lang = userSettings.language || to.params.lang || 'sk';
    i18n.locale = lang;
    if (to.params.lang !== lang) {
        to.params.lang = lang;
        return next({
            name: to.name,
            params: to.params,
            query: to.query
        });
    }
    // SERVICE WORKER UPDATE
    if (navigator.serviceWorker) {
        navigator.serviceWorker
            .getRegistrations()
            .then((registrationsArray) => registrationsArray[0]?.update())
            .catch((error) => {
            logError(error, TelemetryErrorTypes.NETWORK, 'Service worker update check failed');
        });
    }
    // GRAFANA/FARO SETUP
    setFaroUser(userId, vxDepartmentStore?.selectedDepartment?.id);
    // LOAD DEPARTMENTS
    // We will load also disabled departments for customer, but they will be disabled in selections
    let departments = getDepartments();
    if (!departments) {
        if (userRole === SystemUserRoles.CUSTOMER) {
            let appFilter = AppConfig.getConfig()?.app.identifier || ApplicationTypes.LOGHUB;
            departments = (await departmentRepository.getDepartments(null, null, ['Application==' + appFilter]))
                .getData()
                .getData();
        }
        else {
            departments = AdminDepartment.convertToUserDepartments((await departmentRepository.getAllDepartmentsAdmin(null, null, [], true)).getData());
        }
        setDepartments(departments);
    }
    // IF NO DEPARTMENTS -> REDIRECT TO ORGANIZATIONS PAGE
    if (userRole === SystemUserRoles.CUSTOMER && getEnabledDepartments(departments).length == 0) {
        if (to.name !== 'organizations' && to.meta.layout !== 'organization') {
            return router.replace({
                name: 'organizations',
                params: {
                    lang: lang
                }
            });
        }
        else {
            return next();
        }
    }
    // STORE SELECTED DEPARTMENT ID
    // If customer has selected department, they must have also favorite
    if (userRole === SystemUserRoles.CUSTOMER) {
        let selectedDepartment = getSelectedDepartment();
        if (!selectedDepartment) {
            let favoriteDepartmentId = userSettings.preferences.favoriteDepartmentId
                ? userSettings.preferences.favoriteDepartmentId
                : departments[0].id;
            let routerObj = checkDepartmentAccessAndSet(userRole, departments, favoriteDepartmentId, to.params.departmentId, lang);
            if (routerObj !== null) {
                return next(routerObj);
            }
        }
    }
    // HOME REDIRECT / ALREADY LOGGED IN
    if (to.name === 'home' || (to.meta.authPage && loggedIn)) {
        if (userRole === SystemUserRoles.CUSTOMER || userRole === null) {
            return router.replace({
                name: 'dashboard',
                params: {
                    lang: lang,
                    departmentId: getSelectedDepartment().id
                }
            });
        }
        else {
            return router.replace({
                name: 'adminCompanies',
                params: {
                    lang: lang
                }
            });
        }
    }
    // CHECK IF ID IN URL IS VALID
    if (to.params.departmentId && !isDepartmentIdValid(to.params.departmentId)) {
        VueUtilities.openErrorToast(i18n.t('error_messages.invalid_department_id').toString());
        if (userRole === SystemUserRoles.CUSTOMER) {
            return next({
                name: 'organizations',
                params: {
                    lang: lang
                }
            });
        }
        else {
            return next({
                name: 'adminCompanies',
                params: {
                    lang: lang
                }
            });
        }
    }
    // When TO-department is disabled
    if (userRole === SystemUserRoles.CUSTOMER &&
        to.params.departmentId &&
        getDepartmentById(departments, to.params.departmentId).disableStatus?.isDisabled) {
        if (to.meta.availableForDisabledDept === true) {
            setSelectedDepartment(departments, to.params.departmentId);
            return next();
        }
        else {
            return router.push({
                name: 'dashboard',
                params: {
                    lang: lang,
                    departmentId: to.params.departmentId
                }
            });
        }
    }
    // USER PERMISSIONS and ROLE
    // Reload when permissions are null, or when user changes department
    if (LoggedUserManager.getPermissions() === null ||
        (userRole === SystemUserRoles.CUSTOMER && to.params.departmentId !== getSelectedDepartment().id)) {
        let permissions = [];
        //TODO: Here we have to check isAdmin flag.
        if (userRole != SystemUserRoles.CUSTOMER) {
            user = user ?? (await userRepository.getCurrentUser());
            const permissionsForRole = user.role.Permissions;
            if (permissionsForRole && permissionsForRole.length > 0) {
                permissions = permissionsForRole;
            }
        }
        else {
            let member = await userRepository.getUserRole(userId, to.params.departmentId);
            permissions = member.Permissions;
            LoggedUserManager.setDepartmentRole(member.Key);
        }
        LoggedUserManager.setPermissions(permissions);
    }
    // IF DEPARTMENT ID IS IN URL, STORE IT
    if (to.params.departmentId) {
        let routerObj = checkDepartmentAccessAndSet(userRole, departments, to.params.departmentId, to.params.departmentId, lang);
        if (routerObj !== null) {
            return next(routerObj);
        }
    }
    // LOGGED IN COMPANY FLAG
    if ((userRole === SystemUserRoles.SYSTEM_ADMIN || userRole === SystemUserRoles.RESELLER_ADMIN) &&
        to.meta.departmentScope &&
        !LoggedUserManager.isLoggedToCompany()) {
        LoggedUserManager.loginToCompany(AdminDepartment.convertToAdminDepartment(getSelectedDepartment()));
    }
    // RELOAD TIER - IF IN COMPANY AND SWITCHING DEPARTMENTS
    let adminInCompany = LoggedUserManager.isAdmin() && LoggedUserManager.isLoggedToCompany();
    if ((adminInCompany || userRole === SystemUserRoles.CUSTOMER) && to.meta.layout === 'with-sidebar') {
        if (from.params.departmentId != to.params.departmentId)
            featuresManager.loadTier();
    }
    // CACHE THINGS
    if (userRole === SystemUserRoles.CUSTOMER && to.params.departmentId) {
        let allPromises = [];
        allPromises.push(eventTypeRepository.getAllTypes(true));
        allPromises.push(userSettingsRepository.loadUserSettingsForDepartment(userId, true));
        allPromises.push(departmentRepository.getDepartmentSettings());
        try {
            await Promise.all(allPromises).then((response) => {
                if (to.name === 'dashboard') {
                    if (response[2].setupRequired.toString() === 'true') {
                        return next({
                            name: 'wizard',
                            params: {
                                lang: lang,
                                departmentId: getSelectedDepartment().id
                            }
                        });
                    }
                }
            });
        }
        catch (error) {
            return;
        }
    }
    // CHECK IF DEPARTMENT ID IS PRESENT IF departmentScope: true
    if (to.meta.departmentScope && !to.params.departmentId) {
        return router.replace({
            name: to.name,
            params: {
                lang: lang,
                departmentId: getSelectedDepartment().id
            }
        });
    }
    return next();
});
export default router;
/**
 * Function serves for setting selected department.
 * It checks if customer is enabled in department. If not, it tries to find the first suitable one
 * and redirects user there.
 * For admins, it directly sets ID from URL (due to their permissions).
 * @param role logged user role
 * @param departmentId favorite department ID / ID of department from URL
 * @param departments user's assigned departments
 * @param toDepId selected department ID
 * @param lang user's preferable language
 * @returns router object if it needs to redirect, otherwise null
 */
function checkDepartmentAccessAndSet(role, departments, departmentId, toDepId, lang) {
    if (role === SystemUserRoles.CUSTOMER && !isDepartmentAvailable(departments, departmentId)) {
        let enabledDepartments = getEnabledDepartments(departments);
        if (enabledDepartments?.length > 0) {
            if (departmentId === toDepId) {
                VueUtilities.openErrorToast(i18n.t('error_messages.user_not_allowed', {
                    dep: getDepartmentById(departments, departmentId).name
                }));
                return {
                    name: 'dashboard',
                    params: {
                        lang: lang,
                        departmentId: enabledDepartments[0].id
                    }
                };
            }
            else {
                setSelectedDepartmentById(enabledDepartments[0].id);
            }
        }
        else {
            VueUtilities.openErrorToast(i18n.t('error_messages.user_without_department'));
            return {
                name: 'organizations',
                params: {
                    lang: lang
                }
            };
        }
    }
    else {
        setSelectedDepartment(departments, departmentId);
    }
    return null;
}
function getDepartments() {
    return vxDepartmentStore.departments;
}
function setDepartments(value) {
    vxDepartmentStore.departments = value;
}
/**
 * Function which checks if exists department id from url
 * @param departmentId
 * @returns true if department exists, else false
 */
function isDepartmentIdValid(departmentId) {
    return getDepartments().find((x) => x.id === departmentId) ? true : false;
}
function getSelectedDepartment() {
    return vxDepartmentStore.selectedDepartment;
}
/**
 * Returns departments where user is enabled
 * @param departments user's assigned departments
 * @returns array of enabled departments
 */
function getEnabledDepartments(departments) {
    return departments ? departments.filter((department) => department.member?.Enabled) : [];
}
function getDepartmentById(departments, departmentId) {
    return departments.find((dep) => dep.id === departmentId);
}
/**
 * Checks if user is enabled in department
 * @param departments user's assigned departments
 * @param departmentId selected department's ID
 * @returns true if yes, false if no
 */
function isDepartmentAvailable(departments, departmentId) {
    let department = getDepartmentById(departments, departmentId);
    return department?.member.Enabled || false;
}
/**
 * Function which initializes selected and favorite department id in store
 * If user has favorite department in preferences, it's also his currently selected
 * If user hasn't favorite department, his currently selected department is first from list of departments
 * @param departments
 * @param favoriteDepartmentId
 * @returns currently selected department
 */
function setSelectedDepartment(departments, favoriteDepartmentId) {
    let selectedDepId = favoriteDepartmentId;
    if (!selectedDepId || (selectedDepId && selectedDepId.length == 0)) {
        selectedDepId = departments[0].id;
    }
    setSelectedDepartmentById(selectedDepId);
    setFavoriteDepartmentById(selectedDepId);
}
function setSelectedDepartmentById(value) {
    vxDepartmentStore.setSelectedDepartmentById(value);
}
function setFavoriteDepartmentById(value) {
    vxDepartmentStore.setFavoriteDepartmentById(value);
}
